import { STATIC_FILTERS } from '@modules/order/constants/uiConstants'
import { DimensionPaginatedHierarchyItemsUIStore } from '@stores/paginated-hierarchy-items/DimensionPaginatedHierarchyItemsUIStore'
import { ProductDataStore } from './ProductDataStore'
import { action, computed, observable } from 'mobx'
import { stores } from '@stores'
import { getChannelName } from '@utils/get-channel-name'
import {
  ENHANCED_HIERARCHY_ELEMENT_HEIGHT,
  ENHANCED_PRODUCT_CARD_DIMENSIONS,
  PRODUCT_IMAGE_HEIGHT
} from '@modules/showroom/productCollection/constants/uiConstants'
import { ItemType } from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsUIStore'
import { computedFn } from 'mobx-utils'
import { filter, find } from 'lodash'

export interface ISize {
  id: string
  name: string
  sizeCode: number
  isInactive?: boolean
}
// Card View
export class ProductUIStore extends DimensionPaginatedHierarchyItemsUIStore<ProductDataStore> {
  @observable currentViewConfig = null
  constructor(public dataStore: ProductDataStore, cardViewDimensions) {
    super(new ProductDataStore(STATIC_FILTERS), cardViewDimensions)
  }

  @action
  init() {
    if (this.isEnhancedViewEnabled) {
      // This method is used to save topbar selections across views
      this.restoreOrdersTopbarSelections()
    }
    super.init()
  }

  @action
  setCurrentViewConfig = () => {
    const {
      nav: {
        queryParams: { clientType }
      },
      productConfiguration: { getViewProductCardAttributes }
    } = stores
    const channelName = getChannelName(clientType)
    this.currentViewConfig = getViewProductCardAttributes(
      channelName,
      this.dataStore.currentViewName
    )
  }

  @action
  updateOrderCardDimensions = () => {
    if (this.dimensions) {
      const newHeight = this.cardDynamicContentHeight + PRODUCT_IMAGE_HEIGHT
      const newDim = {
        [ItemType.CATEGORY]: {
          height: ENHANCED_HIERARCHY_ELEMENT_HEIGHT
        },
        [ItemType.INDIVIDUAL]: {
          ...ENHANCED_PRODUCT_CARD_DIMENSIONS[this.dimensions],
          height: newHeight
        },
        [ItemType.PAGE]: {
          ...ENHANCED_PRODUCT_CARD_DIMENSIONS[this.dimensions],
          height: newHeight
        }
      }
      this.updateViewDimensions(newDim)
    }
  }

  @computed get areObjectsDataLoading() {
    return this.dataStore.itemIndexObjectStore.objectLoadingProgress?.every(map =>
      map?.get('LOADING')
    )
  }

  getBookedDoors = computedFn((productId: string, doorOrders: any) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const bookedDoorOrderIds = filter(
      Object.keys(product?.quantity?.doorProducts || {}),
      doorOrderId => !find(doorOrders, doorOrder => doorOrder.id === doorOrderId)?.deleted
    )
    return bookedDoorOrderIds
  })

  getSizes = computedFn((productId: string): Array<ISize> => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const {
      original: { sizes },
      quantity
    } = product
    return (sizes || [])
      .concat(
        (quantity?.inactiveSizes || []).map(inactiveSize => {
          return { ...inactiveSize, isInactive: true }
        })
      )
      .sort((sizeA, sizeB) => sizeA.sizeCode - sizeB.sizeCode)
  })

  @action
  prepareViewData = () => {
    this.dataStore.fetchAndPrepareFilterAttributesData(this.dataStore.currentViewName)
    this.dataStore.cnfGroupByUIStore.setGroupByAttributes(this.dataStore.currentViewName)
    this.dataStore.enhancedSortStore.setSortAttributesList(this.dataStore.currentViewName)
    this.calculateCardHeight(this.currentViewConfig)
    this.updateOrderCardDimensions()
  }

  @action
  destroy = () => {
    this.dataStore.destroy()
  }
}
